// Modal de downgrade de assinatura — Story 3.5.
// Diferenças vs UpgradeModal: filtra preço MENOR, sem cobrança ponte,
// agenda pra fim do ciclo.

import * as Dialog from '@radix-ui/react-dialog';
import { CalendarClock, TrendingDown, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { formatBR, parseLocalDate } from '@control-center/shared';
import type { DowngradePreviewResponse } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Select } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { usePlans } from '../../hooks/use-plans';
import { usePreviewDowngrade, useScheduleDowngrade } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';
import type { SubscriptionDetail } from '../../lib/subscriptions-api';

interface DowngradeModalProps {
  subscription: SubscriptionDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatBRL(amountString: string): string {
  const n = Number(amountString);
  if (!Number.isFinite(n)) return amountString;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function DowngradeModal({
  subscription,
  open,
  onOpenChange,
}: DowngradeModalProps): JSX.Element | null {
  const [toPlanId, setToPlanId] = useState<string>('');
  const [preview, setPreview] = useState<DowngradePreviewResponse['data'] | null>(null);
  const previewMutation = usePreviewDowngrade();
  const scheduleMutation = useScheduleDowngrade();
  const plansQ = usePlans({ limit: 100 });

  useEffect(() => {
    if (!open) {
      setToPlanId('');
      setPreview(null);
      previewMutation.reset();
      scheduleMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!subscription) return null;

  const currentPrice = Number(subscription.price ?? '0');
  // Elegíveis: ativo + mesmo tipo + preço < atual
  const eligiblePlans = (plansQ.data?.items ?? []).filter((p) => {
    if (!subscription.plan) return false;
    if (p.id === subscription.plan.id) return false;
    if (p.type !== subscription.plan.type) return false;
    if (Number(p.monthlyPrice) >= currentPrice) return false;
    return true;
  });

  async function handlePreview(planId: string): Promise<void> {
    if (!planId || !subscription) {
      setPreview(null);
      return;
    }
    try {
      const result = await previewMutation.mutateAsync({
        id: subscription.id,
        payload: { to_plan_id: planId },
      });
      setPreview(result);
    } catch (err) {
      setPreview(null);
      toast.error(extractErrorMessage(err, 'Não foi possível calcular o downgrade.'));
    }
  }

  async function handleConfirm(): Promise<void> {
    if (!toPlanId || !subscription) return;
    try {
      const result = await scheduleMutation.mutateAsync({
        id: subscription.id,
        payload: { to_plan_id: toPlanId },
      });
      const dateLabel = formatBR(parseLocalDate(result.scheduledFor));
      toast.success('Downgrade agendado.', {
        description: `Aplica em ${dateLabel}. Cliente mantém acesso atual até lá.`,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos agendar o downgrade.'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(560px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="flex items-center gap-2 text-[18px] font-semibold tracking-[-0.005em] text-ink">
                <TrendingDown size={16} strokeWidth={1.8} className="text-muted" />
                Trocar de plano (downgrade)
              </Dialog.Title>
              <p className="mt-1 text-[12px] text-muted">
                {subscription.client.name} · Plano atual:{' '}
                <strong>{subscription.plan?.name ?? '—'}</strong> ({formatBRL(subscription.price)})
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            className="flex flex-col gap-4"
          >
            <Field
              label="Plano alvo"
              required
              hint="Apenas planos do mesmo tipo, ativos e com preço menor que o atual"
            >
              {({ inputId }) => (
                <Select
                  id={inputId}
                  value={toPlanId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setToPlanId(v);
                    void handlePreview(v);
                  }}
                  disabled={plansQ.isLoading || eligiblePlans.length === 0}
                >
                  <option value="">— selecione —</option>
                  {eligiblePlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {formatBRL(p.monthlyPrice)}/mês
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {eligiblePlans.length === 0 && !plansQ.isLoading && (
              <div className="rounded-[10px] border border-line bg-panel-2 p-3 text-[12px] text-muted">
                Nenhum plano elegível pra downgrade (mesmo tipo + preço menor).
              </div>
            )}

            {previewMutation.isPending && (
              <div className="rounded-[10px] border border-line bg-panel-2 p-3 text-[12px] text-muted">
                Calculando agendamento…
              </div>
            )}
            {preview && (
              <div className="rounded-[12px] border border-line bg-panel-2 p-4">
                <div className="mb-3 flex items-center gap-2 text-[12px] uppercase tracking-wider text-muted-3">
                  <CalendarClock size={13} strokeWidth={2} />
                  Preview do agendamento
                </div>
                <dl className="grid grid-cols-2 gap-y-2 text-[13px]">
                  <dt className="text-muted">De</dt>
                  <dd className="text-right text-ink tnum">{formatBRL(preview.fromPrice)}/mês</dd>
                  <dt className="text-muted">Pra</dt>
                  <dd className="text-right text-ink tnum">{formatBRL(preview.toPrice)}/mês</dd>
                  <dt className="text-muted">Aplica em</dt>
                  <dd className="text-right text-ink tnum">
                    {formatBR(parseLocalDate(preview.scheduledFor))}
                  </dd>
                  <dt className="text-muted">Dias restantes</dt>
                  <dd className="text-right text-ink tnum">{preview.daysRemaining}</dd>
                  <dt className="font-medium text-ink">Economia mensal</dt>
                  <dd className="text-right text-[15px] font-semibold text-good tnum">
                    {formatBRL(preview.monthlySavings)}
                  </dd>
                </dl>
                <p className="mt-3 rounded-[8px] border border-warn/30 bg-warn/5 p-2 text-[11px] leading-[1.5] text-ink-2">
                  Cliente mantém acesso ao plano <strong>atual</strong> até{' '}
                  {formatBR(parseLocalDate(preview.scheduledFor))}. <strong>Sem reembolso</strong>{' '}
                  da diferença. Próxima cobrança já vai pelo preço do plano novo.
                </p>
              </div>
            )}

            <div className="mt-2 flex items-center justify-end gap-2">
              <PillButton type="button" onClick={() => onOpenChange(false)}>
                Cancelar
              </PillButton>
              <PillButton
                type="submit"
                solid
                disabled={!preview || scheduleMutation.isPending || previewMutation.isPending}
              >
                {scheduleMutation.isPending ? 'Agendando…' : 'Agendar downgrade'}
              </PillButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
